import { useEffect } from 'react';
import { useStateValue } from '../context/AppContext';

/**
 * Hook to initialize the analytics tracking. Multiple initializations are not
 * problematic.
 *
 * @remarks
 *
 * Continually pushing payloads into adobeDataLayer is not a problem.
 * Nested values are all merged, last one wins. You can verify this payload
 * by extracting `kiwiEddlHelper.getState('adl')` within the UI console.
 */
export function useAnalytics() {
  const { orgEvent, user } = useStateValue();
  useEffect(() => {
    window.adobeDataLayer = window.adobeDataLayer || [];
    const orgId = orgEvent?.orgId;
    const role = user?.role;
    if (orgId && role) {
      window.adobeDataLayer.push({
        user: {
          app: {
            orgId,
            role,
          },
        },
      });
    }
    window?._satellite?.track?.('cbTrack.viewInDom');
  }, [orgEvent?.orgId, user?.role]);
}

/**
 * Hook to track page changes for analytics.
 */
export function useAnalyticsPageChange(pathname = '', errorCode = '') {
  useEffect(() => {
    window.adobeDataLayer ??= [];
    const newPathName = shortenPathName(pathname);
    const page: { page: string; pageCode: string; errorCode?: string } = {
      page: newPathName,
      pageCode: newPathName,
    };
    if (errorCode) {
      page.errorCode = errorCode;
    }
    window.adobeDataLayer.push?.({
      page,
    });
  }, [pathname, errorCode]);
}

const pathnamesThatNeedToBeShortened = [
  { pattern: new RegExp('^(/irregularities/get).*'), replacementPattern: '$1' },
  { pattern: new RegExp('^(/rooms/edit).*'), replacementPattern: '$1' },
  { pattern: new RegExp('^(/rooms/get)/[^\\/]*(/(.*))?'), replacementPattern: '$1$2' },
  { pattern: new RegExp('^(/staff/edit).*'), replacementPattern: '$1' },
  { pattern: new RegExp('^(/staff/get).*'), replacementPattern: '$1' },
  { pattern: new RegExp('^(/students/get).*'), replacementPattern: '$1' },
];

/**
 * Analytics for each individual entity is not useful. This
 * utility will shorten a pathname, so that we can group all
 * queries for a particular component into a common bucket.
 * If a match is not found, returns the original string
 *
 * @example
 * // returns 'students/get'
 * shortenPathName('students/get/2a3facb5-8234-4c59-af96-6ff297d56b41')
 * // returns 'rooms/get'
 * shortenPathName('rooms/get/0b25b76d-03a8-4da1-a60c-cfe6a0f988f6')
 * // returns 'some/unique/path'
 * shortenPathName('some/unique/path')
 */
export function shortenPathName(pathname: string) {
  let newPathName = pathname;
  for (const { pattern, replacementPattern } of pathnamesThatNeedToBeShortened) {
    if (pattern.test(pathname)) {
      newPathName = pathname.replace(pattern, replacementPattern);
    }
  }
  // removes the '/' from all pathNames
  if (newPathName.startsWith('/')) {
    newPathName = newPathName.slice(1);
  }
  return newPathName;
}
